import { createContext, useCallback, useContext } from 'react';
import { useApolloClient } from '@apollo/client/react';
import type { ItemMatch } from '@/components/shared/FolderItemList/reconcileInCache';
import { reconcileItemsInCache } from '@/components/shared/FolderItemList/reconcileInCache';
import { useFolderItemScrollStore } from '@/stores/folder-item-scroll-store';

/** Handler for a single newly created item. Also accepts a batch array (batch upload).
 *  skipScroll: pin item at row 0 immediately without triggering the scroll/highlight signal.
 *  Use when an Edit dialog opens right after creation — pass scrollToId to that dialog so
 *  EditScreenDialog.useEffect fires the signal after the dialog closes instead.
 *  asset: full item object — when provided, stored in newItemObjects so displayAssets can
 *  render it from the store even when the Apollo cache has been evicted. */
export type NewItemHandler = (
  id: string | string[],
  options?: { skipScroll?: boolean; asset?: unknown },
) => void;

export const NewItemContext = createContext<NewItemHandler | null>(null);

/** Consume the nearest NewItemContext — returns null when rendered outside a Provider. */
export function useNewItemHandler(): NewItemHandler | null {
  return useContext(NewItemContext);
}

/**
 * Returns a NewItemHandler that writes the new item(s) to position 0 of the Apollo cache
 * via cache.modify (same mechanism as reconcileGroupsInCache for folders) and fires the
 * scroll + pin signals so the item stays at row 0 through subsequent refetches.
 *
 * Accepts a single id or an array of ids (batch upload). For a batch, all items are
 * prepended at row 0 and the scroll signal is sent for all ids at once.
 */
export function useNewItemCacheHandler(
  rootField: string,
  newItemTypename: string,
  match: ItemMatch,
): NewItemHandler {
  const client = useApolloClient();
  return useCallback<NewItemHandler>(
    (idOrIds, options) => {
      const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
      ids.forEach((id) =>
        reconcileItemsInCache(client.cache, { rootField, newItemTypename, newItemId: id, match }),
      );
      const items = options?.asset != null ? [options.asset] : undefined;
      const storeState = useFolderItemScrollStore.getState();
      if (options?.skipScroll) {
        storeState.requestScrollToNewItems(ids, items, { skipScroll: true });
      } else {
        storeState.requestScrollToNewItems(ids, items);
      }
    },
    // match is an object literal from the render — spread primitives to keep dep stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, rootField, newItemTypename, match.path, match.teamId, match.engage],
  );
}
